/** Shared glossary for multi-agent Fleet Control setup (fleet map + guide). */

export type FleetConceptTerm = {
  id: string;
  title: string;
  definition: string;
};

export const FLEET_SYSTEM_CONCEPTS: FleetConceptTerm[] = [
  {
    id: "gateway",
    title: "Gateway",
    definition:
      "The Jaisiu process you connect to from Chat. It loads config, runs agents, and exposes RPC methods (chat, config, agents.fleet.graph).",
  },
  {
    id: "agent",
    title: "Agent",
    definition:
      "A configured worker identity in agents.list (id, workspace, model, tools, skills). Each agent has its own workspace files and can spawn subagents when allowed.",
  },
  {
    id: "session",
    title: "Session",
    definition:
      "A running conversation transcript keyed like agent:main:main. Chat sends messages to the active session; subagents get child session keys.",
  },
  {
    id: "subagent",
    title: "Subagent",
    definition:
      "A child session spawned by sessions_spawn from a parent agent. Shown as parallel runs in chat and as bright live edges on the fleet map.",
  },
  {
    id: "channel",
    title: "Channel",
    definition:
      "An external messaging integration (Telegram, Slack, WhatsApp, …). Channels deliver inbound messages; routing may use workforce bindings or separate channel bindings in config.",
  },
  {
    id: "skill",
    title: "Skill",
    definition:
      "Reusable instruction pack the agent can load on demand (from bundled, managed, or workspace skills/). Per-agent skills[] is an allowlist.",
  },
  {
    id: "tool",
    title: "Tool",
    definition:
      "Built-in capabilities (read, exec, browser, sessions_spawn, …). Tool policy (profile, allow/deny) limits what an agent may call.",
  },
  {
    id: "workspace",
    title: "Workspace",
    definition:
      "Directory for an agent (agents.list[].workspace). Bootstrap files here (SOUL.md, AGENTS.md, …) are injected into the system prompt each session.",
  },
  {
    id: "bootstrap",
    title: "Bootstrap files",
    definition:
      "SOUL.md (persona), AGENTS.md (operating rules), IDENTITY.md, USER.md, TOOLS.md — edited under Behavior in the fleet editor or Agents → Files.",
  },
  {
    id: "edge-spawn",
    title: "Solid arrow (spawn)",
    definition: "Agent A may delegate to agent B via subagents.allowAgents (or * for any).",
  },
  {
    id: "edge-route",
    title: "Dashed arrow (route)",
    definition:
      "Inbound → agent from workforce.bindings (keywords, priority). External channel traffic can be routed here.",
  },
  {
    id: "edge-live",
    title: "Bright arrow (live)",
    definition: "Active subagent or session link in the current chat — not stored in config.",
  },
  {
    id: "work-plan",
    title: "Work plan",
    definition:
      "Agents → Work plan: decompose an epic into context-sized units, optional YouTrack parent + subtasks, then sessions_spawn one subagent per unit.",
  },
  {
    id: "workforce-binding",
    title: "Workforce binding",
    definition:
      "workforce.bindings[] entry: bindingId, target agentId, match keywords, priority. Edited via Inbound routes on the map.",
  },
  {
    id: "channel-binding",
    title: "Channel binding (config)",
    definition:
      "Separate top-level bindings[] routes a channel/account to an agent. Not drawn on the fleet map yet — use Channels + Config for channel-specific routing.",
  },
];

/** ASCII fleet topology for Help → How it works (and fleet guide). */
export const FLEET_ARCHITECTURE_ASCII = `
┌──────────────────────────────────────────────────────────────────────────────┐
│ FLEET CONTROL (browser)                                                      │
│  Chat · Sessions · Agents · Channels · Config · Help                         │
└────────────────────────────────────┬─────────────────────────────────────────┘
                                     │  WebSocket / HTTP
                                     ▼
┌──────────────────────────────────────────────────────────────────────────────┐
│ GATEWAY                                                                      │
│  openclaw.json · sessions.* · chat · agents.fleet.graph                      │
└───────────────┬──────────────────────────────────────────────┬─────────────┘
                │ config (dotted on map)                         │ live runs
                ▼                                                ▼
    ┌───────────────────────┐                         ┌─────────────────────┐
    │  CHANNELS             │  - - - - - - - - - - ▶  │  INBOUND ROUTER       │
    │  Telegram · Slack ·   │      dashed route       │  workforce.bindings[] │
    │  WhatsApp · Web · …   │                         └──────────┬──────────┘
    └───────────────────────┘                                    │
                                                                   ▼
              ┌────────────────────────────────────────────────────────────┐
              │  AGENT  (agents.list[] — id, workspace, model, tools, skills) │
              └───────────────┬───────────────────────────────┬────────────────┘
                              │                               │
         · · · · · · · · · · ·│                               │─────── solid spawn
         │ SOUL.md AGENTS.md   │                               │       (subagents.allowAgents)
         │ skills[] tools[]    │                               ▼
         │ model               │                    ┌──────────────────────┐
         └─────────────────────┘                    │  SUBAGENT SESSION    │
                              │                      │  (child transcript)  │
                              ▼                      └──────────────────────┘
                    ┌─────────────────────┐
                    │  SESSION            │
                    │  agent:<id>:main    │
                    │  agent:<id>:fork:…  │
                    └─────────────────────┘

  Session key navigator (Chat header):
    agent  :  main  :  fork  :  <uuid>
      │        │        │          └── row 4 · id
      │        │        └───────────── row 3 · kind (main | fork | help | …)
      │        └────────────────────── row 2 · agent (+ New agent · Agents)
      └─────────────────────────────── row 1 · scope (always agent)

  Fleet map edge legend:
    ───▶  solid   configured spawn (subagents.allowAgents)
    - - ▶  dashed  inbound route (workforce.bindings)
    ███▶  bright  live subagent for the open chat session only
`.trim();

/** How Fleet Control tabs map to runtime objects (Chat · Sessions · Agents · Channels · Subagents). */
export const FLEET_CONTROL_RELATIONSHIPS_ASCII = `
  CHAT · SESSIONS · AGENTS · CHANNELS · SUBAGENTS
  ═══════════════════════════════════════════════

  ┌─────────────────┐     ┌─────────────────┐     ┌─────────────────┐
  │ CHANNELS tab    │     │ AGENTS tab      │     │ SESSIONS tab    │
  │ Integrations    │     │ agents.list     │     │ All keys on     │
  │ Telegram·Slack  │     │ workspace files │     │ gateway · patch │
  │ WhatsApp·Web…   │     │ tools · skills  │     │ labels · delete │
  └────────┬────────┘     └────────┬────────┘     └────────┬────────┘
           │                       │                       │
           │  inbound message      │  who can run          │  admin view
           │  (external user)      │  (config, not chat)   │  of every thread
           ▼                       │                       │
  ┌────────────────────────────────┴───────────────────────┘
  │  ROUTING (config)                                           │
  │  workforce.bindings[]  ·  channels.*  ·  bindings[]         │
  └────────────────────────────┬────────────────────────────────┘
                               │ picks target agent
                               ▼
                    ┌─────────────────────┐
                    │  AGENT (worker)     │◀──── row 2 in Chat session bar
                    │  e.g. main, coder   │      (+ New agent · Agents)
                    └──────────┬──────────┘
                               │ one active run at a time
                               ▼
  ┌────────────────────────────────────────────────────────────┐
  │  CHAT tab (Chat)                                            │
  │  · Session tabs / key navigator (main · fork · …)           │
  │  · Composer → chat.send on active sessionKey                │
  │  · Tabs: Chat · Subagent activity · Projects · Files · Map    │
  └────────────────────────────┬───────────────────────────────┘
                               │
              ┌────────────────┴────────────────┐
              │  SESSION (live transcript)       │
              │  agent:<agentId>:main              │
              │  agent:<agentId>:fork:<uuid>       │
              │  listed in Sessions tab + open     │
              │  as tabs when you switch in Chat   │
              └────────────────┬───────────────────┘
                               │ sessions_spawn (tool)
                               │ parent stays in Chat
                               ▼
                    ┌─────────────────────┐
                    │  SUBAGENT           │
                    │  child sessionKey   │
                    │  own tab in Chat    │
                    │  bright edge on map │
                    │  ← Back to parent   │
                    └─────────────────────┘

  Relationships in one line:
    Channels ──route──▶ Agent ──runs──▶ Session ◀──managed in── Sessions tab
                              ▲              │
                              │              └── you work in ──▶ CHAT tab
                              │                         │
                              └──────── spawns ─────────┘ SUBAGENT (child session)

  Where to click:
    Outside world     →  Channels (+ Config bindings)
    Define workers    →  Agents  (+ New agent)
    Browse all keys   →  Sessions
    Talk & delegate   →  Chat (pick session, send, fork, spawn subagents)
`.trim();
